/**
 * Reports: the Zoho Books report set, for these books.
 *
 * One list of every report this person may open, grouped as Zoho groups them,
 * and one viewer that draws any of them from the shape the backend sends
 * (`reporting/pack/shape.py`): columns, rows with an indent and a style, and
 * links into the ledger behind a figure. The filters live in the address, so a
 * drill-down is a link and a report can be bookmarked. "Download Excel" asks
 * for the same report as a file — the same object, so the two cannot differ.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, formatInr, type Location } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Loading, Note } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

interface Spec {
  key: string
  title: string
  group: string
  takes: string[]
  zoho: string
  blurb: string
}

interface Column {
  key: string
  label: string
  kind: 'text' | 'date' | 'money' | 'number' | 'percent'
}

interface Row {
  cells: Record<string, string | number | null>
  level: number
  style: '' | 'group' | 'subtotal' | 'total' | 'muted'
  link: { report: string; params: Record<string, string> } | null
}

interface Report {
  key: string
  title: string
  company: string
  period: string
  basis: string | null
  filters: { label: string; value: string }[]
  notes: string[]
  columns: Column[]
  rows: Row[]
}

interface Named {
  id: string
  name: string
}

const GROUP_ORDER = [
  'Business overview',
  'Accountant',
  'Payables',
  'Purchases and expenses',
  'Sales',
  'Receivables',
  'Taxes',
  'Activity',
]

const KEYS = [
  'status',
  'since',
  'upto',
  'as_at',
  'day',
  'location_id',
  'department_id',
  'party_id',
  'account_id',
  'user_id',
] as const

function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fyStart(d: Date) {
  return new Date(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1, 3, 1)
}

//: The windows people ask for, in the words they ask for them.
function presets(): { label: string; since: string; upto: string }[] {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const q = Math.floor(((m + 9) % 12) / 3) // 0 = Apr–Jun
  const qStart = (n: number) => {
    const fy = fyStart(now).getFullYear()
    const month = 3 + n * 3
    return new Date(fy + Math.floor(month / 12), month % 12, 1)
  }
  const fy = fyStart(now)
  return [
    { label: 'This month', since: iso(new Date(y, m, 1)), upto: iso(now) },
    {
      label: 'Last month',
      since: iso(new Date(y, m - 1, 1)),
      upto: iso(new Date(y, m, 0)),
    },
    { label: 'This quarter', since: iso(qStart(q)), upto: iso(now) },
    {
      label: 'Last quarter',
      since: iso(q === 0 ? new Date(fy.getFullYear() - 1, 0, 1) : qStart(q - 1)),
      upto: iso(new Date(qStart(q).getTime() - 86400000)),
    },
    { label: 'This year', since: iso(fy), upto: iso(now) },
    {
      label: 'Last year',
      since: iso(new Date(fy.getFullYear() - 1, 3, 1)),
      upto: iso(new Date(fy.getFullYear(), 2, 31)),
    },
  ]
}

function ddmmyyyy(value: string) {
  const [y, m, d] = value.slice(0, 10).split('-')
  return d && m && y ? `${d}/${m}/${y}` : value
}

function cell(column: Column, value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return ''
  if (column.kind === 'money') return formatInr(Number(value))
  if (column.kind === 'date') return ddmmyyyy(String(value))
  if (column.kind === 'percent') return `${Number(value).toFixed(1)}%`
  return String(value)
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

export default function ReportLibrary() {
  const specs = useQuery({
    queryKey: ['report-pack'],
    queryFn: () => api.get<Spec[]>('/reports/pack'),
  })
  const [find, setFind] = useState('')
  const groups = useMemo(() => {
    const needle = find.trim().toLowerCase()
    const shown = (specs.data ?? []).filter(
      (s) =>
        !needle || s.title.toLowerCase().includes(needle) || s.blurb.toLowerCase().includes(needle),
    )
    return GROUP_ORDER.map((g) => ({ group: g, specs: shown.filter((s) => s.group === g) })).filter(
      (g) => g.specs.length > 0,
    )
  }, [specs.data, find])

  return (
    <>
      <Card title="Reports" hint="every report opens on screen and downloads as Excel">
        <input
          aria-label="Find a report"
          placeholder="Find a report…"
          value={find}
          onChange={(e) => setFind(e.target.value)}
        />
      </Card>
      <ErrorNote error={specs.error} />
      {specs.isLoading ? (
        <Loading what="Loading the reports" />
      ) : (
        groups.map((g) => (
          <Card key={g.group} title={g.group}>
            <div className="report-list">
              {g.specs.map((s) => (
                <Link key={s.key} to={`/books/report-library/${s.key}`} className="report-link">
                  <strong>{s.title}</strong>
                  <span className="muted">{s.blurb}</span>
                </Link>
              ))}
            </div>
          </Card>
        ))
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// One report
// ---------------------------------------------------------------------------

export function ReportPage() {
  const { key = '' } = useParams()
  const [search, setSearch] = useSearchParams()
  const navigate = useNavigate()
  const { can } = useAuth()

  const specs = useQuery({
    queryKey: ['report-pack'],
    queryFn: () => api.get<Spec[]>('/reports/pack'),
  })
  const spec = specs.data?.find((s) => s.key === key)
  const takes = new Set(spec?.takes ?? [])

  const qs = new URLSearchParams()
  for (const k of KEYS) {
    const v = search.get(k)
    if (v) qs.set(k, v)
  }
  const query = qs.toString()

  const report = useQuery({
    queryKey: ['report-pack', key, query],
    queryFn: () => api.get<Report>(`/reports/pack/${key}${query ? `?${query}` : ''}`),
    enabled: !!spec && (!takes.has('party_required') || !!search.get('party_id')),
  })

  const set = (changes: Record<string, string>) => {
    const next = new URLSearchParams(search)
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    setSearch(next, { replace: true })
  }

  const today = iso(new Date())
  const since = search.get('since') ?? iso(fyStart(new Date()))
  const upto = search.get('upto') ?? today

  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/org/locations'),
    enabled: takes.has('location'),
  })
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Named[]>('/org/departments'),
    enabled: takes.has('department'),
  })
  const wantsParty = takes.has('party') || takes.has('party_required')
  const parties = useQuery({
    queryKey: ['parties', 'all-kinds'],
    queryFn: async () => {
      const [others, directors, investments] = await Promise.all([
        api.get<{ id: string; name: string; code: string }[]>('/parties'),
        api.get<{ id: string; name: string; code: string }[]>('/parties?kind=director'),
        api.get<{ id: string; name: string; code: string }[]>('/parties?kind=investment'),
      ])
      return [...others, ...directors, ...investments]
    },
    enabled: wantsParty,
  })
  const accounts = useQuery({
    queryKey: ['coa-accounts'],
    queryFn: () =>
      api.get<{ id: string; code: string; name: string; is_postable: boolean }[]>('/coa/accounts'),
    enabled: takes.has('account'),
  })
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ id: string; display_name: string }[]>('/org/users'),
    enabled: takes.has('user'),
  })

  const download = `/api/v1/reports/pack/${key}/xlsx${query ? `?${query}` : ''}`
  const downloadPdf = `/api/v1/reports/pack/${key}/pdf${query ? `?${query}` : ''}`
  const r = report.data

  return (
    <>
      <Card
        title={spec?.title ?? 'Report'}
        hint={spec?.zoho ? `Zoho report ${spec.zoho}` : undefined}
        actions={
          <div className="row-actions">
            <Link to="/books/report-library">All reports</Link>
            {can('report.any.export') && r && (
              <>
                <a className="btn primary" href={download}>
                  Download Excel
                </a>
                <a className="btn" href={downloadPdf}>
                  Download PDF
                </a>
              </>
            )}
          </div>
        }
      >
        {spec?.blurb && (
          <p className="muted" style={{ marginTop: 0 }}>
            {spec.blurb}
          </p>
        )}
        <div className="row">
          {takes.has('period') && (
            <>
              <label>
                <span className="caption">From</span>
                <input type="date" value={since} onChange={(e) => set({ since: e.target.value })} />
              </label>
              <label>
                <span className="caption">To</span>
                <input type="date" value={upto} onChange={(e) => set({ upto: e.target.value })} />
              </label>
              <label>
                <span className="caption">Period</span>
                <select
                  value=""
                  onChange={(e) => {
                    const p = presets().find((x) => x.label === e.target.value)
                    if (p) set({ since: p.since, upto: p.upto })
                  }}
                >
                  <option value="">Choose…</option>
                  {presets().map((p) => (
                    <option key={p.label}>{p.label}</option>
                  ))}
                </select>
              </label>
            </>
          )}
          {takes.has('as_at') && (
            <label>
              <span className="caption">As of</span>
              <input
                type="date"
                value={search.get('as_at') ?? today}
                onChange={(e) => set({ as_at: e.target.value })}
              />
            </label>
          )}
          {takes.has('day') && (
            <label>
              <span className="caption">Day</span>
              <input
                type="date"
                value={search.get('day') ?? today}
                onChange={(e) => set({ day: e.target.value })}
              />
            </label>
          )}
          {takes.has('location') && (
            <label>
              <span className="caption">Clinic</span>
              <select
                value={search.get('location_id') ?? ''}
                onChange={(e) => set({ location_id: e.target.value })}
              >
                <option value="">All clinics</option>
                {(locations.data ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {takes.has('department') && (
            <label>
              <span className="caption">Department</span>
              <select
                value={search.get('department_id') ?? ''}
                onChange={(e) => set({ department_id: e.target.value })}
              >
                <option value="">All departments</option>
                {(departments.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {wantsParty && (
            <label>
              <span className="caption">
                {takes.has('party_required') ? 'Vendor, customer or director' : 'Party'}
              </span>
              <SearchSelect
                aria-label="Party"
                value={search.get('party_id') ?? ''}
                onChange={(v) => set({ party_id: v })}
                empty={takes.has('party_required') ? undefined : 'Any party'}
                placeholder="Choose…"
                options={(parties.data ?? []).map((p) => ({
                  value: p.id,
                  label: p.code ? `${p.name} (${p.code})` : p.name,
                }))}
              />
            </label>
          )}
          {takes.has('account') && (
            <label>
              <span className="caption">Account</span>
              <SearchSelect
                aria-label="Account"
                value={search.get('account_id') ?? ''}
                onChange={(v) => set({ account_id: v })}
                empty="Every account"
                options={(accounts.data ?? [])
                  .filter((a) => a.is_postable)
                  .map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }))}
              />
            </label>
          )}
          {takes.has('user') && (
            <label>
              <span className="caption">Person</span>
              <select
                value={search.get('user_id') ?? ''}
                onChange={(e) => set({ user_id: e.target.value })}
              >
                <option value="">Everyone</option>
                {(users.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {takes.has('status') && (
            <label>
              <span className="caption">Status</span>
              <select
                value={search.get('status') ?? ''}
                onChange={(e) => set({ status: e.target.value })}
              >
                <option value="">Still owing</option>
                <option value="unpaid">Unpaid</option>
                <option value="partially_paid">Partly paid</option>
                <option value="overdue">Overdue</option>
                <option value="paid">Paid</option>
                <option value="all">Every bill</option>
              </select>
            </label>
          )}
        </div>
      </Card>

      <ErrorNote error={specs.error ?? report.error} />
      {specs.data && !spec && <Note kind="warn">This report is not open to your role.</Note>}
      {takes.has('party_required') && !search.get('party_id') && (
        <Note kind="info">
          Choose the vendor, customer, director or investment to see its ledger.
        </Note>
      )}
      {report.isLoading && report.fetchStatus !== 'idle' && <Loading what="Drawing the report" />}

      {r && (
        <Card wide tight>
          <div className="report-head">
            <div className="report-company">{r.company}</div>
            <div className="report-title">{r.title}</div>
            {r.basis && <div className="muted">Basis: {r.basis}</div>}
            <div className="muted">{r.period}</div>
            {r.filters.length > 0 && (
              <div className="muted">
                {r.filters.map((f) => `${f.label}: ${f.value}`).join(' · ')}
              </div>
            )}
          </div>
          {r.notes.length > 0 && (
            <div style={{ padding: '0 16px' }}>
              {r.notes.map((n) => (
                <Note
                  key={n}
                  kind={/does not balance|out of balance|unexplained/i.test(n) ? 'error' : 'info'}
                >
                  {n}
                </Note>
              ))}
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table className="report-table">
              <thead>
                <tr>
                  {r.columns.map((c) => (
                    <th
                      key={c.key}
                      className={c.kind === 'money' || c.kind === 'number' ? 'num' : ''}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.rows.length === 0 && (
                  <tr>
                    <td colSpan={r.columns.length} className="faint">
                      Nothing for this period.
                    </td>
                  </tr>
                )}
                {r.rows.map((row, i) => (
                  <tr key={i} className={row.style ? `rep-${row.style}` : undefined}>
                    {r.columns.map((c, j) => {
                      const text = cell(c, row.cells[c.key])
                      const numeric =
                        c.kind === 'money' || c.kind === 'number' || c.kind === 'percent'
                      return (
                        <td
                          key={c.key}
                          className={numeric ? 'num' : c.kind === 'date' ? 'date' : undefined}
                          style={
                            j === 0 && row.level ? { paddingLeft: 12 + row.level * 18 } : undefined
                          }
                        >
                          {j === 0 && row.link && text ? (
                            <a
                              href={`/books/report-library/${row.link.report}?${new URLSearchParams(row.link.params)}`}
                              onClick={(e) => {
                                e.preventDefault()
                                const link = row.link!
                                navigate(
                                  `/books/report-library/${link.report}?${new URLSearchParams(link.params)}`,
                                )
                              }}
                            >
                              {text}
                            </a>
                          ) : (
                            text
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  )
}
